import { modelFactory, PayMethodQuoteModel, SwapCryptoQuoteModel } from '@kano/models';
import QuoteClient from '@kano/network/quote';
import type {
    BuyCryptoQuote,
    RequestBuyCryptoQuote,
    RequestSellCryptoQuote,
    RequestSwapCryptoQuote,
    SellCryptoQuote,
} from '@kano/types';
import UserService from '@/services/UserService';
import { i18n } from '@kano/i18n';

function paymentMethodsModelFactory(quote: BuyCryptoQuote) {
    return {
        ...quote,
        paymentMethods: quote.paymentMethods.map(paymentMethod => {
            const paymentMethodError = quote.paymentMethodErrors?.find(error =>
                error.paymentMethod === paymentMethod.id,
            ) ?? null;

            return new PayMethodQuoteModel(paymentMethod, paymentMethodError);
        }),
    };
}

function payoutMethodsModelFactory(quote: SellCryptoQuote) {
    return {
        ...quote,
        payoutMethods: quote.payoutMethods.map(payoutMethods => {
            const paymentMethodError = quote.payoutMethodErrors?.find(error =>
                error.payoutMethod === payoutMethods.id,
            ) ?? null;

            return new PayMethodQuoteModel(payoutMethods, paymentMethodError);
        }),
    };
}

export class QuoteValidationError extends Error {
    constructor(message: string) {
        super(message);
        this.name = 'QuoteValidationError';
    }
}

export default {
    async listBuyCryptoQuote(payload: RequestBuyCryptoQuote) {
        const userType = await UserService.getUserType();
        return QuoteClient
            .listBuyCryptoQuote({ ...payload, userType })
            .then(paymentMethodsModelFactory)
            .catch((axiosError) => {
                const { paymentMethod } = payload;
                const { data } = axiosError;
                if (!data) {
                    throw axiosError;
                }
                const { paymentMethodErrors } = data;
                if (!paymentMethodErrors || !paymentMethodErrors.length) {
                    throw new QuoteValidationError(i18n.t('exchange-form.error.unexpected-payment-method-error').toString());
                }
                const errorMessage = paymentMethodErrors.find((error: { paymentMethod: string, message: string }) => {
                    return error.paymentMethod === paymentMethod;
                })?.error.message ?? paymentMethodErrors[0].error.message;

                throw new QuoteValidationError(errorMessage);
            });
    },
    async listSellCryptoQuote(payload: RequestSellCryptoQuote) {
        const userType = await UserService.getUserType();
        return QuoteClient
            .listSellCryptoQuote({ ...payload, userType })
            .then(payoutMethodsModelFactory)
            .catch((axiosError) => {
                const { payoutMethod } = payload;
                const { data } = axiosError;
                if (!data) {
                    throw axiosError;
                }
                const { payoutMethodErrors } = data;
                if (!payoutMethodErrors || !payoutMethodErrors.length) {
                    throw new QuoteValidationError(i18n.t('exchange-form.error.unexpected-payment-method-error').toString());
                }
                const errorMessage = payoutMethodErrors.find((error: { payoutMethod: string, message: string }) => {
                    return error.payoutMethod === payoutMethod;
                })?.error.message ?? payoutMethodErrors[0].error.message;

                throw new QuoteValidationError(errorMessage);
            });
    },
    async listSwapCryptoQuote(payload: RequestSwapCryptoQuote) {
        const userType = await UserService.getUserType();
        return QuoteClient
            .listSwapCryptoQuote({ ...payload, userType })
            .then(modelFactory(SwapCryptoQuoteModel))
            .catch((axiosError) => {
                const { payoutMethod } = payload;
                const { data } = axiosError;
                if (!data) {
                    throw axiosError;
                }
                const { payoutMethodErrors } = data;
                if (!payoutMethodErrors || !payoutMethodErrors.length) {
                    throw new QuoteValidationError(i18n.t('exchange-form.error.unexpected-payment-method-error').toString());
                }
                const errorMessage = payoutMethodErrors.find((error: { payoutMethod: string, message: string }) => {
                    return error.payoutMethod === payoutMethod;
                })?.error.message ?? payoutMethodErrors[0].error.message;

                throw new QuoteValidationError(errorMessage);
            });
    },
};
